const mirrorPreviewsKey = "mirror-preview-apps";
const syncSignalKey = "mirror-preview-sync";

type MirrorListener = () => void;
const listeners = new Set<MirrorListener>();

const readMirror = (): { [key: string]: string } => {
  try {
    const rawData = localStorage.getItem(mirrorPreviewsKey);
    return !!rawData ? (JSON.parse(rawData) as { [key: string]: string }) : {};
  } catch {
    return {};
  }
};

const writeMirror = (mirrorPreview: { [key: string]: string }): void => {
  try {
    localStorage.setItem(mirrorPreviewsKey, JSON.stringify(mirrorPreview));
  } catch {
    console.error("Local Storage unavailable");
  }
};

let storageBound = false;

const handleStorageSignal = (event: StorageEvent) => {
  if (event.key !== syncSignalKey || !event.newValue) return;
  listeners.forEach((listener) => listener());
};

const bindStorageListener = () => {
  if (storageBound || typeof window === "undefined") return;
  window.addEventListener("storage", handleStorageSignal);
  storageBound = true;
};

export const updatePreviewMirror = (preview: string, appId: number): void => {
  const mirrorPreview = readMirror();
  mirrorPreview[appId] = preview;

  writeMirror(mirrorPreview);
};

export const mergePreviewMirror = (
  patch: { [key: string]: unknown },
  appId: number,
): void => {
  const mirrorPreview = readMirror();

  let stored: { [key: string]: unknown } = {};
  try {
    const rawEntry = mirrorPreview[appId];
    if (!!rawEntry) stored = JSON.parse(rawEntry) as { [key: string]: unknown };
  } catch {
    stored = {};
  }

  mirrorPreview[appId] = JSON.stringify({ ...stored, ...patch });

  writeMirror(mirrorPreview);
};

export const getPreviewMirror = (appId: number): string => {
  const mirrorPreview = readMirror();
  return !!mirrorPreview[appId] ? mirrorPreview[appId] : "";
};

export const notifyPreviewMirrorSync = (): void => {
  listeners.forEach((listener) => listener());

  try {
    localStorage.setItem(syncSignalKey, JSON.stringify({ ts: Date.now() }));
  } catch {
    return;
  }
};

export const subscribeToPreviewMirror = (
  listener: MirrorListener,
): (() => void) => {
  listeners.add(listener);
  bindStorageListener();
  return () => {
    listeners.delete(listener);
  };
};

const syncingKeys = new Set<string>();
const syncStateListeners = new Set<MirrorListener>();

const notifyPreviewSyncState = () => {
  syncStateListeners.forEach((listener) => listener());
};

export const startPreviewSync = (key: string | number): void => {
  const syncKey = `${key}`;
  if (syncingKeys.has(syncKey)) return;
  syncingKeys.add(syncKey);
  notifyPreviewSyncState();
};

export const endPreviewSync = (key: string | number): void => {
  if (!syncingKeys.delete(`${key}`)) return;
  notifyPreviewSyncState();
};

export const isPreviewSyncing = (key: string | number): boolean =>
  syncingKeys.has(`${key}`);

export const subscribeToPreviewSyncState = (
  listener: MirrorListener,
): (() => void) => {
  syncStateListeners.add(listener);
  return () => {
    syncStateListeners.delete(listener);
  };
};
